import React, { useState, useCallback } from 'react';
import { 
  Card, Button, Form, Alert, Row, Col, 
  ProgressBar, InputGroup, Modal, Table, Badge
} from 'react-bootstrap';
import axios from 'axios';

// Maximum rows allowed in CIBIL CSV file
const MAX_ROWS = 1000000; // 10 lakh rows

const CibilUploadPage = () => {
  const [file, setFile] = useState(null);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [warningMessage, setWarningMessage] = useState('');
  const [showTooLargeModal, setShowTooLargeModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [fileValidated, setFileValidated] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  
  // Get API base URL and token
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
    };
  };

  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';
  
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    
    if (!selectedFile) return;
    
    setFile(selectedFile);
    validateCibilCsvFile(selectedFile);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a file to upload.');
      return;
    }
    
    if (!fileValidated) {
      setError('Please wait for file validation to complete.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${apiBaseUrl}/cibil_upload/cibil-data`,
        formData,
        {
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      
      setUploadStatus(response.data);
    } catch (err) {
      console.error('Error uploading CIBIL data:', err);
      const errorMessage = err.response?.data?.detail || 'An error occurred while uploading the file. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  const handleDrop = (e) => {
    e.preventDefault();
    
    const droppedFile = e.dataTransfer.files[0];
    
    if (!droppedFile) return;
    
    setFile(droppedFile);
    validateCibilCsvFile(droppedFile);
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
  };
  
  const resetUpload = () => {
    setFile(null);
    setUploadStatus(null);
    setError('');
    setWarningMessage('');
    setFileValidated(false);
    setValidationResult(null);
    setPreviewData(null);
    setRowCount(0);
  };
  
  const validateCibilCsvFile = useCallback(async (file) => {
    if (!file) return;
    
    setError('');
    setWarningMessage('');
    setFileValidated(false);
    setValidating(true);
    
    if (!file.name.endsWith('.csv')) {
        setError('Please upload a CSV file with encrypted CIBIL data.');
        setFile(null);
        setValidating(false);
        return;
    }
    
    try {
        const formData = new FormData();
        formData.append('file', file);
        
        // Use POST instead of GET for file validation
        const response = await axios.post(
        `${apiBaseUrl}/cibil_upload/cibil-data/validate`,
        formData,
        {
            headers: {
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data',
            }
        }
        );
        
        const validation = response.data.validation;
        const preview = response.data.preview;
        
        setValidationResult(validation);
        setPreviewData(preview);
        setRowCount(validation.row_count);
        
        if (!validation.valid) {
        setError(validation.error);
        setFile(null);
        setValidating(false);
        return;
        }
        
        if (validation.row_count > MAX_ROWS) {
        setShowTooLargeModal(true);
        setValidating(false);
        return;
        }
        
        // Show validation details
        if (validation.unmapped_columns > 0) {
        setWarningMessage(
            `Note: ${validation.unmapped_columns} columns are not mapped. ${validation.mapping_percentage.toFixed(1)}% of columns are recognized.`
        );
        }
        
        setFileValidated(true);
        setValidating(false);
        
    } catch (err) {
        console.error('Error validating CIBIL CSV:', err);
        setError('Error validating file. Please check the format and try again.');
        setFile(null);
        setValidating(false);
    }
    }, [apiBaseUrl]);

  const handleDownloadTemplate = async () => {
    try {
      const response = await axios.get(
        `${apiBaseUrl}/cibil_upload/cibil-data/template`,
        {
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'cibil_data_template.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      setError('Failed to download template. Please try again.');
    }
  };
  
  const handleCloseTooLargeModal = () => {
    setShowTooLargeModal(false);
    setFile(null);
  };

  const showValidationDetails = () => {
    setShowValidationModal(true);
  };

  return (
    <div>
      <h2>Upload CIBIL Data</h2>
      <p className="text-muted">Upload encrypted CIBIL data in CSV format to update lead profiles.</p>
      
      {/* Modal for files with too many rows */}
      <Modal show={showTooLargeModal} onHide={handleCloseTooLargeModal}>
        <Modal.Header closeButton>
          <Modal.Title>File Too Large</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>The CSV file contains {rowCount.toLocaleString()} rows, which exceeds the maximum limit of {MAX_ROWS.toLocaleString()} rows.</p>
          <p>Please split your data into multiple smaller files before uploading.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleCloseTooLargeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Validation Details Modal */}
      <Modal show={showValidationModal} onHide={() => setShowValidationModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Column Mapping Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previewData && (
            <>
              <h6>Mapped Columns ({validationResult?.mapped_columns || 0})</h6>
              <Table size="sm" className="mb-3">
                <thead>
                  <tr>
                    <th>Encrypted Name</th>
                    <th>Original Name</th>
                    <th>Database Table</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.column_info
                    .filter(col => col.mapped)
                    .slice(0, 10)
                    .map((col, index) => (
                    <tr key={index}>
                      <td><code>{col.encrypted_name}</code></td>
                      <td>{col.original_name}</td>
                      <td><Badge bg="info">{col.table}</Badge></td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              
              {validationResult?.unmapped_columns > 0 && (
                <>
                  <h6>Unmapped Columns ({validationResult.unmapped_columns})</h6>
                  <p className="text-muted small">
                    First 10 unmapped columns: {previewData.column_info
                      .filter(col => !col.mapped)
                      .slice(0, 10)
                      .map(col => col.encrypted_name)
                      .join(', ')}
                  </p>
                </>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowValidationModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
      
      <Card className="mb-4">
        <Card.Body>
          {uploadStatus ? (
            <>
              <Alert variant="success">
                <Alert.Heading>CIBIL Data Upload Started!</Alert.Heading>
                <p>{uploadStatus.message}</p>
                <p><strong>File ID:</strong> {uploadStatus.csv_id}</p>
              </Alert>
              
              <Row className="text-center">
                <Col>
                  <h5>Total Records</h5>
                  <p className="display-6">{uploadStatus.total_records}</p>
                </Col>
                <Col>
                  <h5>Processing Chunks</h5>
                  <p className="display-6 text-info">{uploadStatus.total_chunks}</p>
                </Col>
                <Col>
                  <h5>Mapped Columns</h5>
                  <p className="display-6 text-success">{uploadStatus.validation_info.mapped_columns}</p>
                </Col>
                <Col>
                  <h5>Mapping %</h5>
                  <p className="display-6 text-primary">{uploadStatus.validation_info.mapping_percentage.toFixed(1)}%</p>
                </Col>
              </Row>
              
              <div className="d-grid gap-2 mt-4">
                <Button variant="primary" onClick={resetUpload}>
                  Upload Another File
                </Button>
              </div>
            </>
          ) : (
            <Form onSubmit={handleSubmit}>
              {error && <Alert variant="danger">{error}</Alert>}
              
              <div 
                className="upload-box mb-3"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                style={{
                  border: '2px dashed #dee2e6',
                  borderRadius: '8px',
                  padding: '2rem',
                  textAlign: 'center',
                  backgroundColor: '#f8f9fa'
                }}
              >
                {file ? (
                  <div className="text-center">
                    <h5 className="mb-3">{file.name}</h5>
                    <p className="text-muted">
                      {(file.size / 1024).toFixed(2)} KB • {validating ? 'Validating...' : fileValidated ? 'Valid CIBIL CSV' : 'Validation pending'}
                    </p>
                    {fileValidated && validationResult && (
                      <div className="mb-3">
                        <Alert variant="info">
                          <i className="bi bi-check-circle-fill me-2"></i>
                          CIBIL CSV validated successfully. {rowCount.toLocaleString()} rows found.
                          <br />
                          <small>
                            {validationResult.mapped_columns} mapped columns ({validationResult.mapping_percentage.toFixed(1)}%)
                            {validationResult.unmapped_columns > 0 && (
                              <span className="text-warning"> • {validationResult.unmapped_columns} unmapped</span>
                            )}
                          </small>
                          <br />
                          <Button variant="link" size="sm" onClick={showValidationDetails}>
                            View Details
                          </Button>
                        </Alert>
                      </div>
                    )}
                    {warningMessage && (
                      <div className="mb-3">
                        <Alert variant="warning">{warningMessage}</Alert>
                      </div>
                    )}
                    <Button 
                      variant="outline-danger"
                      size="sm"
                      onClick={() => resetUpload()}
                    >
                      Remove File
                    </Button>
                  </div>
                ) : (
                  <div className="text-center">
                    <i className="bi bi-cloud-arrow-up-fill fs-1"></i>
                    <h5 className="mt-3">Drag & Drop CIBIL CSV Here</h5>
                    <p className="text-muted">Or click to browse encrypted CIBIL data file</p>
                    <InputGroup className="mt-3 justify-content-center">
                      <Form.Control
                        type="file"
                        accept=".csv"
                        onChange={handleFileChange}
                        style={{ maxWidth: '300px' }}
                      />
                    </InputGroup>
                  </div>
                )}
              </div>
              
              <div className="d-grid gap-2">
                <Button 
                  type="submit"
                  variant="primary"
                  disabled={!file || loading || validating || !fileValidated}
                >
                  {loading ? 'Uploading CIBIL Data...' : validating ? 'Validating...' : 'Upload CIBIL Data'}
                </Button>
              </div>
              
              {loading && (
                <ProgressBar animated now={100} className="mt-3" />
              )}
            </Form>
          )}
        </Card.Body>
      </Card>
      
      <Card>
        <Card.Body>
          <Card.Title>CIBIL Data Format</Card.Title>
          <Card.Text>
            Your CSV file must contain encrypted CIBIL column headers (max {MAX_ROWS.toLocaleString()} rows):
          </Card.Text>
          <ul>
            <li><strong>telephone</strong> (Required - Mobile number)</li>
            <li><strong>ID_number</strong> (Required - PAN)</li>
            <li><strong>name, email, address</strong> (Personal details)</li>
            <li><strong>Encrypted CIBIL fields</strong> (dm001s, at01s, g102s_cc, etc.)</li>
          </ul>
          <Card.Text>
            The system will automatically map encrypted columns to the appropriate database tables
            and update existing leads or create new ones based on mobile/PAN matching.
          </Card.Text>
          <div className="mt-3">
            <Button 
              variant="outline-primary" 
              size="sm"
              onClick={handleDownloadTemplate}
            >
              <i className="bi bi-download me-1"></i>
              Download CIBIL Template CSV
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default CibilUploadPage;